import { useState, useCallback } from "react";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { View } from "@common/types/views/views";
import {
    ColumnData,
    determineSectionFromDropId,
    calculateInsertionIndex,
    separateColumnsByPin,
} from "./columnSettingsUtils";
import { useColumnSettingsSensors } from "./useColumnSettingsSensors";

interface UseDragAndDropColumnsProps {
    /** Every column of the view, in order; a drag moves one among them. */
    columns: View["columns"];
    /** The rows that drag, a subset of `columns`. */
    visibleColumns: ColumnData[];
    onColumnsChange: (columns: View["columns"]) => void;
}

export function useDragAndDropColumns({
    columns,
    visibleColumns,
    onColumnsChange,
}: UseDragAndDropColumnsProps) {
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useColumnSettingsSensors();

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(String(event.active.id));
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
    }, []);

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const { active, over } = event;
            setActiveId(null);

            if (!over) {
                return;
            }

            const activeColId = String(active.id);
            const overColId = String(over.id);

            if (activeColId === overColId) {
                return;
            }

            const draggedColumn = visibleColumns.find(
                (col) => col.id === activeColId,
            );
            if (!draggedColumn || draggedColumn.immutable) {
                return;
            }

            const columnSections = separateColumnsByPin(visibleColumns);
            const sectionInfo = determineSectionFromDropId(
                overColId,
                columnSections,
            );
            if (!sectionInfo) {
                return;
            }

            const { targetPin } = sectionInfo;

            const newColumns = [...columns];
            const activeColumnIndex = newColumns.findIndex(
                (col) => col.id === activeColId,
            );
            if (activeColumnIndex === -1) {
                return;
            }

            const activeColumn = newColumns[activeColumnIndex];

            const updatedColumn = {
                ...activeColumn,
                options: {
                    ...activeColumn.options,
                    pin: targetPin,
                },
            };

            const insertionIndex = calculateInsertionIndex(
                overColId,
                activeColumnIndex,
                newColumns,
            );

            newColumns.splice(activeColumnIndex, 1);
            newColumns.splice(insertionIndex, 0, updatedColumn);

            onColumnsChange(newColumns);
        },
        [visibleColumns, columns, onColumnsChange],
    );

    return {
        activeId,
        sensors,
        handleDragStart,
        handleDragEnd,
        handleDragCancel,
    };
}
